import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "src/components/pageHeader";
import { displayDate } from "src/utils/dateHandler.util";
import { timeAgo } from "src/utils/date.util";
import { getLangNameByLangCode } from "src/utils/util";
import { FaArrowLeft, FaClock, FaUser, FaHashtag } from "react-icons/fa";
import { Slot } from "../types/slot.entity";
import { useGetBookingsBySlotIdQuery } from "src/features/bookings/bookings.service";

type SlotDetailsProps = {
  slot: (Slot & { id: string });
  onBack?: () => void;
};

export function SlotDetails({ slot, onBack }: SlotDetailsProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const { data: bookings, isLoading: isBookingLoading } = useGetBookingsBySlotIdQuery(slot.id, { skip: !slot?.id });

  const scheduledAt = (slot as any).date?.toDate?.() as Date | undefined;
  const createdAt = (slot as any).createdAt?.toDate?.() as Date | undefined;

  const baseItemClasses = "transform transition-all duration-300 ease-out";
  const hiddenItemClasses = "opacity-0 translate-y-1";
  const visibleItemClasses = "opacity-100 translate-y-0";
  const cx = (extra: string = "") =>
    `${baseItemClasses} ${isVisible ? visibleItemClasses : hiddenItemClasses} ${extra}`.trim();
  const delayStyle = (ms: number) => ({ transitionDelay: `${ms}ms` } as React.CSSProperties);

  const statusBadgeClass = () => {
    switch (slot.status) {
      case "AVAILABLE":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "BOOKED":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "EXPIRED":
        return "bg-gray-100 text-gray-700 border-gray-200";
      case "SINGLE_REQUEST_PENDING":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const coachTypeClass = () =>
    slot.coachType === "PRO"
      ? "bg-yellow-100 text-yellow-800 border-yellow-200"
      : "bg-teal-100 text-teal-700 border-teal-200";

  return (
    <div className="page_container">
      <PageHeader text="Slot Details" />

      {onBack && (
        <div className="mb-4">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
          >
            <FaArrowLeft />
            Back
          </button>
        </div>
      )}

      <div
        className={
          "bg-white rounded-xl shadow p-6 transform transition-all duration-300 ease-out " +
          (isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95")
        }
      >
        {/* Header badges */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span
            className={cx(`inline-block px-3 py-1 rounded-full border text-xs font-semibold ${statusBadgeClass()}`)}
            style={delayStyle(60)}
          >
            {slot.status.replaceAll("_", " ")}
          </span>
          <span
            className={cx(`inline-block px-3 py-1 rounded-full border text-xs font-semibold ${coachTypeClass()}`)}
            style={delayStyle(120)}
          >
            {slot.coachType}
          </span>
          <span
            className={cx(
              "inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
            )}
            style={delayStyle(180)}
          >
            <FaHashtag /> {slot.requestedLearnersIds?.length ?? 0} requests
          </span>
          <span
            className={cx(
              "inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
            )}
            style={delayStyle(220)}
          >
            <FaHashtag /> {slot.offeredLearnersIds?.length ?? 0} offers
          </span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={cx("flex items-start gap-3")} style={delayStyle(80)}>
            <FaHashtag className="mt-1 text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Slot ID</div>
              <div className="font-mono text-sm">{slot.id}</div>
            </div>
          </div>

          <div className={cx("flex items-start gap-3")} style={delayStyle(120)}>
            <FaUser className="mt-1 text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">User</div>
              <Link
                to={`/users/${slot.userId}`}
                className={cx(
                  "inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                )}
                style={delayStyle(140)}
              >
                {slot.userId}
              </Link>
            </div>
          </div>

          <div className={cx("flex items-start gap-3")} style={delayStyle(160)}>
            <FaClock className="mt-1 text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">When</div>
              <div className="font-semibold">{displayDate(slot.date)}</div>
              {scheduledAt && <div className="text-xs text-gray-500">{timeAgo(scheduledAt)}</div>}
            </div>
          </div>

          <div className={cx("flex items-start gap-3")} style={delayStyle(200)}>
            <div className="mt-1 text-gray-500">🌐</div>
            <div>
              <div className="text-sm text-gray-500">Language</div>
              <div className="font-semibold">{getLangNameByLangCode(slot.langCode)}</div>
              <div className="text-xs text-gray-500">{slot.langCode}</div>
            </div>
          </div>

          {slot.groupId && (
            <div className={cx("flex items-start gap-3")} style={delayStyle(240)}>
              <div className="mt-1 text-gray-500">👥</div>
              <div>
                <div className="text-sm text-gray-500">Group ID</div>
                <div className="font-mono text-sm">{slot.groupId}</div>
              </div>
            </div>
          )}

          <div className={cx("flex items-start gap-3")} style={delayStyle(280)}>
            <FaClock className="mt-1 text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Created</div>
              <div className="font-semibold">{displayDate(slot.createdAt)}</div>
              {createdAt && <div className="text-xs text-gray-500">{timeAgo(createdAt)}</div>}
            </div>
          </div>
        </div>

        {/* Requested learners */}
        <div className="mt-8">
          <div className="text-sm text-gray-500 mb-2">Requested Learners</div>
          {slot.requestedLearnersIds?.length ? (
            <div className="flex flex-wrap gap-2">
              {slot.requestedLearnersIds.map((id, index) => (
                <Link
                  key={id}
                  to={`/users/${id}`}
                  className={cx(
                    "inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  )}
                  style={delayStyle(100 + index * 30)}
                >
                  {id}
                </Link>
              ))}
            </div>
          ) : (
            <p className={cx("text-gray-500")} style={delayStyle(120)}>
              None
            </p>
          )}
        </div>

        {/* Offered learners */}
        <div className="mt-6">
          <div className="text-sm text-gray-500 mb-2">Offered Learners</div>
          {slot.offeredLearnersIds?.length ? (
            <div className="flex flex-wrap gap-2">
              {slot.offeredLearnersIds.map((id, index) => (
                <Link
                  key={id}
                  to={`/users/${id}`}
                  className={cx(
                    "inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  )}
                  style={delayStyle(120 + index * 30)}
                >
                  {id}
                </Link>
              ))}
            </div>
          ) : (
            <p className={cx("text-gray-500")} style={delayStyle(140)}>
              None
            </p>
          )}
        </div>
      </div>

      {/* Booking Details (second card under the slot card) */}
      <div
        className={
          "mt-6 bg-white rounded-xl shadow p-6 transform transition-all duration-300 ease-out " +
          (isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95")
        }
      >
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="text-lg font-semibold">Bookings ({bookings?.length ?? 0})</div>
        </div>

        {isBookingLoading ? (
          <div className="text-gray-600">Loading...</div>
        ) : bookings?.length ? (
          <div className="space-y-4">
            {bookings.map((b) => {
              const bWhen = (b as any)?.date?.toDate?.() as Date | undefined;
              const bCreated = (b as any)?.createdAt?.toDate?.() as Date | undefined;
              return (
                <div key={b.id} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium bg-blue-50 text-blue-700 border-blue-200">
                        {b.bookingStatus}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
                        {b.coachType}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium bg-cyan-50 text-cyan-700 border-cyan-200">
                        {b.langCode} • {getLangNameByLangCode(b.langCode)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 font-semibold">Booking</span>
                      <Link
                        to={`/bookings/${b.id}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white border border-emerald-300 shadow-sm hover:shadow-md hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1 select-text"
                        title="Open booking page"
                      >
                        {b.id}
                      </Link>
                    </div>
                  </div>

                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Slot ID</div>
                      <div className="font-mono text-xs">{b.slotId}</div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Coach:</span>
                      <Link
                        to={`/users/${b.coachId}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                      >
                        {b.coachId}
                      </Link>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Learner:</span>
                      <Link
                        to={`/users/${b.learnerId}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                      >
                        {b.learnerId}
                      </Link>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500">When</div>
                      <div className="text-sm font-semibold">{displayDate(b.date)}</div>
                      {bWhen && <div className="text-xs text-gray-500">{timeAgo(bWhen)}</div>}
                    </div>

                    <div>
                      <div className="text-xs text-gray-500">Created</div>
                      <div className="text-sm font-semibold">{displayDate(b.createdAt)}</div>
                      {bCreated && <div className="text-xs text-gray-500">{timeAgo(bCreated)}</div>}
                    </div>

                    {(b.note || b.sessionId || b.organizationId != null || b.projectId != null) && (
                      <div className="md:col-span-2">
                        <details className="group">
                          <summary className="cursor-pointer select-none text-sm text-gray-700 font-semibold hover:underline">
                            More details
                          </summary>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {b.sessionId && (
                              <div>
                                <div className="text-xs text-gray-500">Session ID</div>
                                <div className="font-mono text-xs">{b.sessionId}</div>
                              </div>
                            )}
                            {b.organizationId != null && (
                              <div>
                                <div className="text-xs text-gray-500">Organization ID</div>
                                <div className="font-mono text-xs">{String(b.organizationId)}</div>
                              </div>
                            )}
                            {b.projectId != null && (
                              <div>
                                <div className="text-xs text-gray-500">Project ID</div>
                                <div className="font-mono text-xs">{String(b.projectId)}</div>
                              </div>
                            )}
                            {b.note && (
                              <div className="md:col-span-2">
                                <div className="text-xs text-gray-500">Note</div>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">{b.note}</div>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-600">No booking found for this slot.</div>
        )}
      </div>
    </div>
  );
}

export default SlotDetails;


